<script setup>
import { ref } from 'vue';
import lensHelpFeedbackApi from '@/common/api/lensHelpFeedback.api';

// Thumbs up or down on one finished Lens Help answer (#6535). Optimistic: the choice shows at once and
// goes back if the save fails. Choosing the active thumb again removes the rating; a thumbs-down
// offers a reason.

const props = defineProps({
    turnId: { type: String, required: true },
    accountId: { type: String, required: true },
});

const REASONS = [
    { value: 'wrong_guide', label: 'Wrong guide' },
    { value: 'not_answered', label: "Didn't answer my question" },
    { value: 'out_of_date', label: 'Out of date' },
    { value: 'other', label: 'Other' },
];

const rating = ref(null);
const reason = ref(null);
const error = ref('');

async function save(nextRating, nextReason) {
    const previous = { rating: rating.value, reason: reason.value };
    rating.value = nextRating;
    reason.value = nextReason;
    error.value = '';
    try {
        if (nextRating) await lensHelpFeedbackApi.rate(props.accountId, props.turnId, nextRating, nextReason);
        else await lensHelpFeedbackApi.clearRating(props.accountId, props.turnId);
    } catch (failure) {
        rating.value = previous.rating;
        reason.value = previous.reason;
        error.value = "Couldn't save your rating";
        console.error('Lens answer rating could not be saved', { turnId: props.turnId, rating: nextRating, reason: nextReason, error: failure });
    }
}

function chooseThumb(value) {
    save(rating.value === value ? null : value, null);
}

function chooseReason(value) {
    save('down', value);
}
</script>

<template>
    <div class="d-flex flex-column ga-1" data-cy="lensAnswerRating">
        <div class="d-flex align-center ga-1">
            <span class="text-body-2 text-medium-emphasis mr-1" data-cy="lensAnswerRatingPrompt">Was this helpful?</span>
            <v-btn
                icon
                variant="text"
                :color="rating === 'up' ? 'primary' : undefined"
                aria-label="Helpful"
                title="Helpful"
                :aria-pressed="rating === 'up'"
                data-cy="lensAnswerRatingUp"
                @click="chooseThumb('up')"
            >
                <font-awesome-icon :icon="[rating === 'up' ? 'fas' : 'far', 'thumbs-up']" size="lg" />
            </v-btn>
            <v-btn
                icon
                variant="text"
                :color="rating === 'down' ? 'primary' : undefined"
                aria-label="Not helpful"
                title="Not helpful"
                :aria-pressed="rating === 'down'"
                data-cy="lensAnswerRatingDown"
                @click="chooseThumb('down')"
            >
                <font-awesome-icon :icon="[rating === 'down' ? 'fas' : 'far', 'thumbs-down']" size="lg" />
            </v-btn>
        </div>
        <div v-if="rating === 'down'" class="d-flex flex-wrap ga-1" data-cy="lensAnswerRatingReasons">
            <v-chip
                v-for="option in REASONS"
                :key="option.value"
                label
                color="primary"
                :variant="reason === option.value ? 'tonal' : 'outlined'"
                :data-cy="`lensAnswerRatingReason-${option.value}`"
                @click="chooseReason(option.value)"
            >
                {{ option.label }}
            </v-chip>
        </div>
        <div v-if="error" class="text-body-2 text-error" role="alert" data-cy="lensAnswerRatingError">{{ error }}</div>
    </div>
</template>
